import { createContext, useContext, useEffect, useRef, useState } from "react";
import Axios from "../Axios";
import consts from "../consts";
import $ from "jquery";

const ChartContext = createContext(null);

const normalizeMarketSymbol = (symbol = "") =>
  String(symbol).replace(/[_/]/g, "").toUpperCase();

const toBinanceStreamSymbol = (symbol = "") =>
  normalizeMarketSymbol(symbol).toLowerCase();

const toDisplaySymbol = (symbol = "") => {
  const rawSymbol = String(symbol).trim().toUpperCase();
  if (!rawSymbol) return "";

  if (rawSymbol.includes("/")) return rawSymbol;
  if (rawSymbol.includes("_")) return rawSymbol.replace("_", "/");
  if (rawSymbol.length === 6) {
    return `${rawSymbol.slice(0, 3)}/${rawSymbol.slice(3)}`;
  }

  return rawSymbol;
};

const buildSymbolAliases = (symbol = "") =>
  [...new Set([symbol, String(symbol).toUpperCase(), toDisplaySymbol(symbol), normalizeMarketSymbol(symbol)])].filter(Boolean);

const formatMarketValue = (rawValue) => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return "0.00000";

  if (typeof rawValue === "string" && rawValue.includes(".")) {
    const decimals = Math.min(rawValue.split(".")[1].length, 8);
    return value.toFixed(decimals);
  }

  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toFixed(5);
};

const ChartProvider = ({ children }) => {
  const socketRef = useRef({ forex: null, commodities: null });
  const priceRef = useRef({});
  const tvWidgetRef = useRef({});
  const lineRefs = useRef({}); // 🟩 Store TP/SL/Bet line IDs per chart
  const commodityAliasRef = useRef({});
  const [forexSymbols, setForexSymbols] = useState([]);
  const [commoditySymbols, setCommoditySymbols] = useState([]);

  const setMarketTicker = (aliases, nextTicker) => {
    aliases.forEach((alias) => {
      priceRef.current[alias] = nextTicker;
    });
  };

  const subscribeForexPairs = (pairs) => {
    if (!pairs.length) return undefined;

    let socket = null;

    try {
      const symbols = pairs
        .map((item) => toDisplaySymbol(item?.pair_symbol))
        .filter(Boolean);

      socket = new WebSocket(
        `wss://ws.twelvedata.com/v1/quotes/price?apikey=${consts.apikey}`,
      );
      socketRef.current.forex = socket;

      socket.onopen = () => {
        console.log("✅ Connected to Forex WS in Chart");
        socket.send(
          JSON.stringify({
            action: "subscribe",
            params: { symbols: symbols.join(",") },
          }),
        );
      };

      socket.onmessage = (event) => {
        if (typeof event.data !== "string") return;

        try {
          const parsedData = JSON.parse(event.data);
          const rawSymbol = parsedData?.symbol;
          const price = Number(parsedData?.price);
          if (!rawSymbol || !Number.isFinite(price) || price <= 0) return;

          const pip =
            String(rawSymbol).toUpperCase().includes("JPY") ? 0.001 : 0.00001;
          const bid = price - pip / 2;
          const ask = price + pip / 2;
          const spread = Number(((ask - bid) / pip).toFixed(1));
          const symbolKey = normalizeMarketSymbol(rawSymbol);
          const nextTicker = {
            symbol: symbolKey,
            ask: Number(ask),
            bid: Number(bid),
            buy: Number(ask),
            sell: Number(bid),
            spread,
            midprice: price,
            midPrice: price,
            timestamp: Number(parsedData?.timestamp || Date.now()),
          };

          setMarketTicker(buildSymbolAliases(rawSymbol), nextTicker);

          $(`.price-${symbolKey}`).text(price.toFixed(5));
          $(`.buy-${symbolKey}`).text(Number(ask).toFixed(5));
          $(`.sell-${symbolKey}`).text(Number(bid).toFixed(5));
          $(`.spread-${symbolKey}`).text(
            Number.isFinite(spread) ? String(spread) : "0",
          );
          $(`.buybtn-${symbolKey}`).text(Number(ask).toFixed(5));
          $(`.sellbtn-${symbolKey}`).text(Number(bid).toFixed(5));
          $(`.spreadchart-${symbolKey}`).text(
            Number.isFinite(spread) ? String(spread) : "0",
          );
        } catch (error) {
          console.log("Forex chart price parse error:", error);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error in Forex Chart Context:", error);
      };

      socket.onclose = (event) => {
        console.log("Forex WS closed in Chart Context", event);

        if (socketRef.current.forex === socket) {
          socketRef.current.forex = null;
        }
      };
    } catch (error) {
      console.log("Forex subscribe error:", error);
    }

    return () => {
      if (socket) {
        try {
          socket.close();
        } catch (error) {
          console.log("Forex socket close error:", error);
        }
      }

      if (socketRef.current.forex === socket) {
        socketRef.current.forex = null;
      }
    };
  };

  const subscribeCommodityPairs = (pairs) => {
    if (!pairs.length) return undefined;

    let socket = null;

    try {
      const symbols = [
        ...new Set(
          pairs
            .map((item) => normalizeMarketSymbol(item?.pair_symbol))
            .filter(Boolean),
        ),
      ];
      if (!symbols.length) return undefined;
      const topics = symbols.map((symbol) => `${toBinanceStreamSymbol(symbol)}@bookTicker`);
      const streamUrl = `wss://stream.binance.com:9443/stream?streams=${topics.join("/")}`;

      socket = new WebSocket(streamUrl);
      socketRef.current.commodities = socket;

      socket.onopen = () => {
        console.log("✅ Connected to Binance Commodities WS");
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const payload = message?.data || message;
          const symbol = normalizeMarketSymbol(
            payload?.s || message?.stream?.split("@")?.[0],
          );
          if (!symbol) return;

          const existingTicker = priceRef.current[symbol] || { symbol };
          console.log(payload, "Current payload");
          const rawAsk = payload?.a;
          const rawBid = payload?.b;
          const rawLast = payload?.c ?? payload?.lastPrice;
          const rawMark = rawLast;

          const ask =
            rawAsk != null ? Number(rawAsk) : Number(existingTicker.ask || 0);
          const bid =
            rawBid != null ? Number(rawBid) : Number(existingTicker.bid || 0);
          const lastPrice =
            rawLast != null
              ? Number(rawLast)
              : Number(
                existingTicker.midprice || existingTicker.markPrice || 0,
              );
          const markPrice =
            rawMark != null
              ? Number(rawMark)
              : Number(existingTicker.markPrice || lastPrice || 0);
          const midprice =
            lastPrice || (ask && bid ? (ask + bid) / 2 : markPrice || 0);
          const spread = ask && bid ? ask - bid : 0;
          const displayAsk = rawAsk ?? (ask > 0 ? ask : midprice);
          const displayBid = rawBid ?? (bid > 0 ? bid : midprice);
          const displayPrice = rawLast ?? rawMark ?? midprice;
          const nextTicker = {
            ...existingTicker,
            symbol,
            ask: ask || existingTicker.ask || midprice || 0,
            bid: bid || existingTicker.bid || midprice || 0,
            buy: ask || existingTicker.ask || midprice || 0,
            sell: bid || existingTicker.bid || midprice || 0,
            spread,
            midprice:
              midprice || existingTicker.midprice || existingTicker.markPrice || 0,
            midPrice:
              midprice || existingTicker.midprice || existingTicker.markPrice || 0,
            markPrice: markPrice || midprice || existingTicker.markPrice || 0,
            timestamp: Number(payload?.E || existingTicker.timestamp || Date.now()),
          };

          setMarketTicker(
            commodityAliasRef.current[symbol] || buildSymbolAliases(symbol),
            nextTicker,
          );

          $(`.price-${symbol}`).text(formatMarketValue(displayPrice));
          $(`.buy-${symbol}`).text(formatMarketValue(displayAsk));
          $(`.sell-${symbol}`).text(formatMarketValue(displayBid));
          $(`.spread-${symbol}`).text(formatMarketValue(spread));
          $(`.buybtn-${symbol}`).text(formatMarketValue(displayAsk));
          $(`.sellbtn-${symbol}`).text(formatMarketValue(displayBid));
          $(`.spreadchart-${symbol}`).text(formatMarketValue(spread));
        } catch (error) {
          console.log("Commodity chart price parse error:", error);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error in Commodity Chart Context:", error);
      };

      socket.onclose = (event) => {
        console.log("Commodity WS closed in Chart Context", event);

        if (socketRef.current.commodities === socket) {
          socketRef.current.commodities = null;
        }
      };
    } catch (error) {
      console.log("Commodity subscribe error:", error);
    }

    return () => {
      if (socket) {
        try {
          socket.close();
        } catch (error) {
          console.log("Commodity socket close error:", error);
        }
      }

      if (socketRef.current.commodities === socket) {
        socketRef.current.commodities = null;
      }
    };
  };

  const getSymbols = async () => {
    const headers = {
      headers: {
        Authorization: localStorage.getItem("nzanzi"),
      },
    };

    const [forexResponse, commodityResponse] = await Promise.allSettled([
      Axios.post("/getPairsByAdmin", { type: "forex" }, headers),
      Axios.post("/getPairsByAdmin", { type: "commodities" }, headers),
    ]);

    if (
      forexResponse.status === "fulfilled" &&
      forexResponse.value?.data?.success
    ) {
      setForexSymbols(forexResponse.value.data.result || []);
    } else {
      setForexSymbols([]);
    }

    if (
      commodityResponse.status === "fulfilled" &&
      commodityResponse.value?.data?.success
    ) {
      const nextCommoditySymbols = commodityResponse.value.data.result || [];
      commodityAliasRef.current = nextCommoditySymbols.reduce((acc, item) => {
        const symbol = normalizeMarketSymbol(item?.pair_symbol);
        if (!symbol) return acc;

        acc[symbol] = buildSymbolAliases(item?.pair_symbol);
        return acc;
      }, {});
      setCommoditySymbols(nextCommoditySymbols);
    } else {
      commodityAliasRef.current = {};
      setCommoditySymbols([]);
    }
  };

  useEffect(() => {
    if (!forexSymbols.length) return undefined;

    return subscribeForexPairs(forexSymbols);
  }, [forexSymbols]);

  useEffect(() => {
    getSymbols();
  }, []);

  useEffect(() => {
    if (!commoditySymbols.length) return undefined;

    return subscribeCommodityPairs(commoditySymbols);
  }, [commoditySymbols]);

  // 🟢 Store line refs
  const tpSlShapes = useRef({ tp: null, sl: null, bet: null });

  // 🟩 Register a chart instance when it mounts
  const registerChart = (chartId, widget) => {
    tvWidgetRef.current[chartId] = widget;
    console.log(chartId, "handleRemoveAllLines");
  };

  // 🟩 Register line references for that chart
  const registerLines = (chartId, lines) => {
    lineRefs.current[chartId] = lines;
  };

  // 🟩 Unregister (cleanup) when chart unmounts
  const unregisterChart = (chartId) => {
    delete tvWidgetRef.current[chartId];
    delete lineRefs.current[chartId];
  };
  // 🟩 Remove only lines for a specific chart
  const handleRemoveAllLines = (chartId) => {
    const widget = tvWidgetRef.current[chartId];
    if (!widget) {
      console.warn(`⚠️ No widget found for chart ${chartId}`);
      return;
    }

    widget.onChartReady(() => {
      const chart = widget.activeChart();
      const shapes = chart.getAllShapes()?.map((shape) => shape?.id);
      try {
        chart.removeAllShapes(); // 🧹 removes TP/SL/Bet lines for that chart only
        shapes.forEach((shape) => chart.removeEntity(shape));
        console.log(`🗑️ Removed all lines for chart ${chartId}`);
      } catch (err) {
        console.warn(`❌ Failed to remove shapes for chart ${chartId}`, err);
      }

      // clean stored refs for this chart
      delete lineRefs.current[chartId];
    });
  };

  return (
    <ChartContext.Provider
      value={{
        socket: socketRef.current,
        priceRef,
        tvWidgetRef,
        tpSlShapes,
        lineRefs,
        registerChart,
        unregisterChart,
        registerLines,
        handleRemoveAllLines,
      }}
    >
      {children}
    </ChartContext.Provider>
  );
};

export const useChartData = () => {
  const context = useContext(ChartContext);
  if (!context)
    throw new Error("useChartData must be used within a ChartProvider");
  return context;
};

export default ChartProvider;
